import React from 'react';
import { Link } from 'react-router-dom';
import GuideLayout from './GuideLayout';
import { notifyError, notifySuccess } from '../../../utils/notify';

// Każda para: [szarfa „dla kogo”, szarfa „od kogo”]
const RIBBON_GROUPS = [
    {
        title: 'Dla mamy',
        pairs: [
            ['Ukochanej Mamie', 'Syn z rodziną'],
            ['Najukochańszej Mamusi', 'Córki z rodzinami'],
            ['Kochanej Mamie - ostatnie pożegnanie', 'Dzieci z rodzinami'],
        ],
    },
    {
        title: 'Dla taty',
        pairs: [
            ['Ukochanemu Tacie', 'Córka z rodziną'],
            ['Kochanemu Tatusiowi', 'Synowie z rodzinami'],
            ['Najlepszemu Tacie - zawsze z nami', 'Dzieci'],
        ],
    },
    {
        title: 'Dla babci',
        pairs: [
            ['Kochanej Babci', 'Wnuki'],
            ['Ukochanej Babci - zawsze w naszych sercach', 'Wnuczka z rodziną'],
            ['Najukochańszej Babci i Prababci', 'Wnuki i prawnuki'],
        ],
    },
    {
        title: 'Dla dziadka',
        pairs: [
            ['Kochanemu Dziadkowi', 'Wnuki'],
            ['Ukochanemu Dziadkowi - pozostaniesz w naszej pamięci', 'Wnuk z rodziną'],
        ],
    },
    {
        title: 'Dla męża lub żony',
        pairs: [
            ['Ukochanemu Mężowi', 'Żona'],
            ['Kochanej Żonie - na zawsze w moim sercu', 'Mąż'],
        ],
    },
    {
        title: 'Dla brata lub siostry',
        pairs: [
            ['Kochanemu Bratu', 'Siostra z rodziną'],
            ['Ukochanej Siostrze - ostatnie pożegnanie', 'Brat z rodziną'],
        ],
    },
    {
        title: 'Dla cioci lub wujka',
        pairs: [
            ['Kochanej Cioci', 'Siostrzenica z rodziną'],
            ['Drogiemu Wujkowi - ostatnie pożegnanie', 'Bratankowie z rodzinami'],
        ],
    },
    {
        title: 'Dla przyjaciela',
        pairs: [
            ['Drogiemu Przyjacielowi', 'Przyjaciele'],
            ['Na zawsze w naszej pamięci', 'Anna i Piotr z dziećmi'],
        ],
    },
    {
        title: 'Od kolegów z pracy i sąsiadów',
        pairs: [
            ['Ostatnie pożegnanie', 'Koleżanki i koledzy z pracy'],
            ['Wyrazy głębokiego współczucia', 'Pracownicy firmy [nazwa]'],
            ['Ostatnie pożegnanie', 'Sąsiedzi'],
        ],
    },
];

const SHORT_SENTENCES = [
    'Ostatnie pożegnanie',
    'Spoczywaj w pokoju',
    'Śpij spokojnie',
    'Pozostaniesz w naszych sercach',
    'Na zawsze w naszej pamięci',
    'Pamiętamy',
    'Z wyrazami współczucia',
    'Kochamy i tęsknimy',
];

const RULES = [
    { title: 'Dwie szarfy, dwie role', text: 'Na jednej szarfie zwykle piszemy, dla kogo jest wieniec lub wiązanka, na drugiej - od kogo.' },
    { title: 'Forma „komu?”', text: 'Adresata piszemy w celowniku: „Ukochanej Mamie”, „Kochanemu Dziadkowi” - nie „Ukochana Mama”.' },
    { title: 'Podpis w mianowniku', text: 'Nadawców piszemy zwyczajnie: „Wnuki”, „Syn z rodziną”, „Koleżanki i koledzy z pracy”.' },
    { title: 'Krótko i czytelnie', text: 'Im krótszy napis, tym większe litery zmieszczą się na szarfie. Dłuższe sentencje lepiej przenieść na kartkę.' },
    { title: 'Wielkie litery z szacunku', text: 'Słowa „Mama”, „Tata”, „Babcia” czy „Przyjaciel” piszemy wielką literą.' },
    { title: 'Sprawdź pisownię', text: 'Przed drukiem upewnij się co do imion, nazwy firmy i polskich znaków - szarfy nie da się poprawić.' },
];

const FAQ = [
    {
        question: 'Co napisać na szarfie od wnuków?',
        answer: 'Najczęściej na jednej szarfie „Kochanej Babci” lub „Kochanemu Dziadkowi”, a na drugiej „Wnuki” albo „Wnuki i prawnuki”. Można dodać krótką sentencję, np. „Na zawsze w naszych sercach”.',
    },
    {
        question: 'Na której szarfie piszemy, od kogo jest wieniec?',
        answer: 'Zwykle jedna szarfa mówi, dla kogo jest wieniec, a druga - od kogo. Kolejność ustala się z kwiaciarnią lub zakładem pogrzebowym; ważne, żeby obie były czytelne po rozłożeniu.',
    },
    {
        question: 'Ile liter zmieści się na szarfie?',
        answer: 'Zależy od długości i szerokości szarfy oraz rozmiaru czcionki. Krótkie napisy, do kilkudziesięciu znaków, pozwalają użyć dużych, czytelnych liter.',
    },
    {
        question: 'Czy na szarfie można napisać imię Zmarłego?',
        answer: 'Można, ale zwykle się tego nie robi - szarfa wskazuje relację („Ukochanej Mamie”), a imię i nazwisko są na tabliczce i klepsydrze.',
    },
];

export default function NapisyNaSzarfy() {
    const copyPair = async ([forWhom, fromWhom]) => {
        try {
            await navigator.clipboard.writeText(`${forWhom}\n${fromWhom}`);
            notifySuccess('Skopiowano napisy na szarfę.');
        } catch {
            notifyError('Nie udało się skopiować - zaznacz tekst i skopiuj go ręcznie.');
        }
    };

    return (
        <GuideLayout
            path='/napisy-na-szarfy'
            title='Napisy na szarfy pogrzebowe - gotowe teksty | Nekrodruk.pl'
            description='Gotowe napisy na szarfy pogrzebowe dla mamy, taty, babci, dziadka, przyjaciela i od kolegów z pracy. Zasady pisania tekstów na szarfy i krótkie sentencje.'
            keywords='napisy na szarfy pogrzebowe, napis na szarfie pogrzebowej, co napisać na szarfie pogrzebowej, napis na wieniec pogrzebowy, szarfa pogrzebowa tekst'
            heading={<>Napisy na szarfy pogrzebowe<em> - gotowe teksty</em></>}
            lead='Nie wiesz, co napisać na szarfie do wieńca lub wiązanki? Zebraliśmy sprawdzone teksty dla najbliższych, przyjaciół i współpracowników oraz zasady, które pomogą uniknąć błędów.'
            faq={FAQ}
        >
            <section className='guide_section'>
                <div className='guide_inner'>
                    <h2 className='guide_title'>Gotowe napisy na szarfy</h2>
                    <p className='guide_lead'>
                        Każdy wiersz to dwie szarfy: <strong>dla kogo</strong> i <strong>od kogo</strong>. Kliknij „Kopiuj”,
                        żeby przekazać tekst kwiaciarni lub zakładowi pogrzebowemu.
                    </p>
                    <div className='guide_cards'>
                        {RIBBON_GROUPS.map((group) => (
                            <article key={group.title} className='guide_card'>
                                <h3>{group.title}</h3>
                                <ul className='guide_ribbons'>
                                    {group.pairs.map((pair) => (
                                        <li key={pair.join('|')} className='guide_ribbon'>
                                            <span className='guide_ribbonText'>{pair[0]}</span>
                                            <span className='guide_ribbonText'>{pair[1]}</span>
                                            <button
                                                type='button'
                                                className='guide_copy'
                                                onClick={() => copyPair(pair)}
                                                aria-label={`Kopiuj napisy: ${pair[0]} - ${pair[1]}`}
                                            >
                                                Kopiuj
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className='guide_section guide_section--soft'>
                <div className='guide_inner'>
                    <h2 className='guide_title'>Krótkie sentencje na szarfę</h2>
                    <p className='guide_lead'>Pasują do każdej relacji - możesz je dodać do imienia adresata albo użyć osobno.</p>
                    <ul className='guide_chips'>
                        {SHORT_SENTENCES.map((sentence) => (
                            <li key={sentence}>{sentence}</li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className='guide_section'>
                <div className='guide_inner'>
                    <h2 className='guide_title'>Jak napisać tekst na szarfę - zasady</h2>
                    <ul className='guide_checklist'>
                        {RULES.map((rule) => (
                            <li key={rule.title}>
                                <strong>{rule.title}</strong>
                                <span>{rule.text}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className='guide_section guide_section--soft'>
                <div className='guide_inner'>
                    <div className='guide_callout'>
                        <div>
                            <h2>
                                Drukujesz szarfy<em> w zakładzie lub kwiaciarni?</em>
                            </h2>
                            <p>
                                W Nekrodruku przygotujesz szarfę z podglądem na żywo: wpisujesz napisy, ustawiasz wymiary i
                                obramowanie, a potem pobierasz gotowy plik PDF. Generator szarf jest dostępny po założeniu
                                konta.
                            </p>
                        </div>
                        <div className='guide_actions'>
                            <Link to='/register' className='guide_btn guide_btn--light'>
                                Załóż konto
                            </Link>
                            <Link to='/drukowanie-szarf-pogrzebowych' className='guide_btn guide_btn--ghost'>
                                Jak drukować szarfy
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </GuideLayout>
    );
}
